import '@/lib/honeycomb';
import { createApp, markRaw } from 'vue';
import nebulaSvg from '@discoveryedu/nebula-icons/nebula.svg';
import I18NextVue from 'i18next-vue';
import { createPinia } from 'pinia';
import i18next from './lib/i18next';
import StudioApp from './StudioApp.vue';
import { createRouter } from './router';
import { bus } from './lib/eventBus';
import {
  getDomains,
  getRegion,
  getEnv,
} from './lib/api';

/**
 * Starts to give us the ability to bootstrap an app anywhere we want.
 * Puts the init function to start everything up into window.studio.init
 * @param {Object} options Options object used to initialize the app
 * @param {Object} options.api Studio api client
 * @param {Object} options.apiProgressBar Studio api client with progress bar
 * @param {Object} options.deApiFetch DE api client
 * @param {Object} options.contentApiFetch Content api client
 * @param {Object} options.domains Object with domains used by app
 * @param {string} options.env Current environment running
 * @param {string} options.region Current region
 */
export default async function initialize(options) {
  // add the bus to the window
  window.studio = {
    /**
     * run it!
     *
     * @param target css selector where the app will be loaded
     * @param appComponentOptions misc options to pass to the application
     */
    init: (target, appComponentOptions) => {
      const app = createApp(
        StudioApp,
        {
          options: appComponentOptions,
        },
      );

      const pinia = createPinia();

      const titanPrefixes = ['studio-', 'manuscript-'];
      const isTitanStore = (id) => titanPrefixes.some((p) => id.startsWith(p));

      // Make our fetch clients available to all Pinia stores
      function PiniaApisPlugin({ store: piniaStore }) {
        if (isTitanStore(piniaStore.$id)) {
          return {
            appApi: markRaw(options.appApi),
            titanApiFetch: markRaw(options.titanApiFetch),
            apiProgressBarFetch: markRaw(options.apiProgressBarFetch),
            deApiFetch: markRaw(options.deApiFetch),
            contentApiFetch: markRaw(options.contentApiFetch),
          };
        }
      }
      pinia.use(PiniaApisPlugin);

      // Make environment settings available to all studio Pinia stores
      function PiniaEnvPlugin({ store: piniaStore }) {
        if (isTitanStore(piniaStore.$id)) {
          return {
            domains: options.domains || getDomains(),
            env: options.env || getEnv(),
            region: options.region || getRegion(),
            studioAppIsRunning: true,
          };
        }
      }
      pinia.use(PiniaEnvPlugin);

      // attach apis
      app.config.globalProperties.$appApi = options.appApi;
      app.config.globalProperties.$titanApiFetch = options.titanApiFetch;
      app.config.globalProperties.$apiProgressBar = options.apiProgressBar;
      app.config.globalProperties.$apiProgressBarFetch = options.apiProgressBarFetch;
      app.config.globalProperties.$deApiFetch = options.deApiFetch;
      app.config.globalProperties.$contentApiFetch = options.contentApiFetch;

      // attach app url
      app.config.globalProperties.$appDomain = options.domains.appDomain;

      // Create the router
      const router = createRouter(pinia);
      app.use(router);

      // Connect templating filters
      app.config.globalProperties.$nebulaSVGPath = nebulaSvg;

      // Prep l10n and create Vue instance. Actual l10n files are loaded in the UPDATE_L10N
      // action after the first handshake with the edde server, so we know what our locale is
      app.use(I18NextVue, { i18next });

      app.use(pinia);

      app.mount(target);
    },
    bus,
    /**
     * External apps can listen to events
     */
    listen: (eventName, callback) => {
      bus.on(eventName, callback);
    },
  };
}
